import { fetchRequest } from '@common/helpers/util/requestMiddleware';
import { writeTerminalLog } from '@common/helpers/log';
import Config from '@skin_config';
import { bisBonanzaAPIPath, bisURL, fallbackBisURL } from '../constants';
import { createBISResponseModel, createBISUserModel } from '../models';

const checkTimeout = (asyncCb, actionName) => (
  (...args) => (
    new Promise(async (resolve, reject) => {
      const timeout = setTimeout(() => {
        writeTerminalLog('warn', `BIS Bonanza API - ${actionName} Failed`, 'Request timeout');
        resolve({
          success: false,
          errors: [{ message: 'Request timeout, Please retry!' }],
        });
      }, 80 * 1000);

      try {
        const result = await asyncCb(...args);
        clearTimeout(timeout);
        resolve(result);
      } catch (e) {
        writeTerminalLog('warn', `BIS Bonanza API - ${actionName} Failed`, e.message);
        clearTimeout(timeout);
        reject(e);
      }
    })
  )
);

const EXTERNAL_VALIDATION_ERROR_CODE = 996;

class BISBonanzaService {
  static baseUrl = bisURL;

  static makeRequest = async (actionName, token, body, options = {}) => {
    const headers = { 'x-api-token': token };

    const fetchFailedResponse = {
      success: false,
      errors: [{ message: 'Something went wrong with validation' }],
    };

    try {
      const res = await fetchRequest(
        this.baseUrl,
        `${bisBonanzaAPIPath}/${actionName}`,
        'POST',
        { headers, body },
        this.baseUrl === fallbackBisURL ? Config.verification.apiFallbackTimeout : Config.verification.apiTimeout
      );

      if (res?.success) {
        return createBISResponseModel({
          customer: createBISUserModel(body, options),
        });
      }

      writeTerminalLog('warn', `BIS Bonanza API - ${actionName} Failed`, res?.errors.map((item) => item.message).join(', ') || '');

      // If we are on primary URL and got a non-external-validation error, try fallback once
      // const isExternalValidationError = res.errors?.some((item) => item.errorCode === EXTERNAL_VALIDATION_ERROR_CODE);
      // if (!isExternalValidationError && this.baseUrl !== fallbackBisURL) {
      //   this.baseUrl = fallbackBisURL;
      //   return this.makeRequest(actionName, token, body, options);
      // }

      return res.errors?.some((item) => item.errorCode === EXTERNAL_VALIDATION_ERROR_CODE)
        ? res
        : fetchFailedResponse;
    } catch (e) {
      writeTerminalLog('warn', `BIS Bonanza API - ${actionName} Failed`, e.message);

      if (e && (e.code === 'ETIMEDOUT' || !e.response) && this.baseUrl !== fallbackBisURL) {
        this.baseUrl = fallbackBisURL;
        return this.makeRequest(actionName, token, body);
      }

      return fetchFailedResponse;
    }
  };

  static validate = checkTimeout((token, body, options = {}) => this.makeRequest('validate', token, body, options), 'validate');
}

export default BISBonanzaService;
